import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import {
  selectIsBuyer,
  selectIsDropshipSetup,
  selectIsOrderPlatformShopify,
} from 'store/selectors/me/company';
import {
  SettingsSubLayout,
  type SettingsSubMenuItem,
} from 'containers/SettingsPage/SettingsLayout';
import useFlagEnabled from 'hooks/useFlagEnabled';

enum SectionSegments {
  Billing = 'billing',
  Shipping = 'shipping',
  Integrations = 'integrations',
  Webhooks = 'webhooks',
  Notifications = 'notifications',
  Merchandising = 'merchandising',
  ServiceLevels = 'service-levels',
  Returns = 'returns',
}

const Dropshipping = () => {
  const { pathname } = useLocation();
  const section = pathname.split('/')[3] as SectionSegments | undefined;

  const isBuyer = useAppSelector(selectIsBuyer);
  const isOrderPlatformShopify = useAppSelector(selectIsOrderPlatformShopify);
  const isDropshipSetup = useAppSelector(selectIsDropshipSetup);
  const showShippingPage = useFlagEnabled('showShippingPage');

  const selectedSection =
    section && Object.values(SectionSegments).includes(section) ? section : SectionSegments.Billing;

  if (!isDropshipSetup) {
    return <Navigate to="/settings/preferences/account" replace />;
  }

  const items: SettingsSubMenuItem[] = [
    {
      label: 'Billing',
      iconName: 'monetization_on',
      to: '/settings/dropshipping/billing',
      isActive: selectedSection === SectionSegments.Billing,
    },
    ...(showShippingPage
      ? [
          {
            label: 'Shipping',
            iconName: 'local_shipping',
            to: '/settings/dropshipping/shipping',
            isActive: selectedSection === SectionSegments.Shipping,
          },
        ]
      : []),
    {
      label: 'Integrations',
      iconName: 'sync_alt',
      to: '/settings/dropshipping/integrations',
      isActive: selectedSection === SectionSegments.Integrations,
    },
    {
      label: 'Webhooks',
      iconName: 'webhook',
      to: '/settings/dropshipping/webhooks',
      isActive: selectedSection === SectionSegments.Webhooks,
    },
    {
      label: 'Notifications',
      iconName: 'notifications',
      to: '/settings/dropshipping/notifications',
      isActive: selectedSection === SectionSegments.Notifications,
    },
    ...(isBuyer
      ? [
          {
            label: 'Merchandising',
            iconName: 'point_of_sale',
            to: '/settings/dropshipping/merchandising',
            isActive: selectedSection === SectionSegments.Merchandising,
          },
          {
            label: 'Service Levels',
            iconName: 'receipt_long',
            to: '/settings/dropshipping/service-levels',
            isActive: selectedSection === SectionSegments.ServiceLevels,
          },
        ]
      : []),
    ...(isOrderPlatformShopify
      ? [
          {
            label: 'Returns',
            iconName: 'assignment_return',
            to: '/settings/dropshipping/returns',
            isActive: selectedSection === SectionSegments.Returns,
          },
        ]
      : []),
  ];

  return <SettingsSubLayout items={items} />;
};

export default Dropshipping;
